import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useNotification } from "../../contexts/NotificationContext";
import { useSearchResults } from "../../hooks/queries/useSearchResults";
import {
  createSavedSearch,
  deleteSavedSearch,
  SavedSearchData,
} from "../../services/savedSearchApi";
import { SearchFilters as ApiSearchFilters } from "../../services/searchApi";
import {
  EntityTypePlural,
  getEntityTypeByPlural,
  SEARCHABLE_ENTITY_TYPES,
} from "../../types/entityRegistry";
import {
  getDbValuesFromDisplayNames as getFacilityStatusDbValues,
  getDisplayNamesFromDbValues as getFacilityStatusDisplayNames,
} from "../../utils/facilityStatusMapping";
import {
  getDbValuesFromDisplayNames,
  getDisplayNamesFromDbValues,
  getDisplayNamesFromDbValues as getFacilityTypeDisplayNames,
} from "../../utils/facilityTypeMapping";
import { getLabelDisplay } from "../../utils/labelMapping";
import { formatProjectVolume } from "../../utils/numberFormatting";
import {
  getDbValuesFromDisplayNames as getOrgCategoryDbValues,
  getDisplayNamesFromDbValues as getOrgCategoryDisplayNames,
} from "../../utils/organizationCategoryMapping";
import {
  getColorForStatus as getOrgStatusColor,
  getDisplayNameFromDbValue as getOrgStatusDisplayName,
} from "../../utils/organizationStatusMapping";
import { projectTypeMapping } from "../../utils/projectTypeMapping";
import { regionMapping } from "../../utils/regionMapping";
import {
  clearAllFiltersFromURL,
  getSearchParamsForSave,
  updateFilterInURL,
} from "../../utils/urlFilterHelpers";
import { FacilityOwnerFilterData } from "../Layout/filters/FacilityOwnerFilter";
import { ProjectOrganizationFilterData } from "../Layout/filters/ProjectOrganizationFilter";
import { RelationshipFilterData } from "../Layout/filters/RelationshipFilter";
import BulkAddToPortfolioButton from "../Portfolio/BulkAddToPortfolioButton";
import BulkAddToTrackButton from "../ResearchWorkflow/BulkAddToTrackButton";
import ConfidenceIndicator from "../common/ConfidenceIndicator";
import ExportFieldsButton from "../common/ExportFieldsButton";
import ProjectStatusChip from "../common/ProjectStatusChip";
import LoadSearchDialog from "./LoadSearchDialog";
import MemoizedSearchInput from "./MemoizedSearchInput";
import SaveSearchDialog from "./SaveSearchDialog";
import SearchFilterPanel from "./SearchFilterPanel";
import { SEARCH_EXPORT_FIELDS, exportSearchResults } from "./searchExport";

// Entity types for search
type EntityType = EntityTypePlural;

interface SearchFilters {
  searchTerm: string;
  entityType: EntityType;
  regions: string[];
  industries: string[];
  facilityTypes: string[];
  organizationTypes: string[];
  projectTypes: string[];
  sourceTypes: string[];
  domains: string[];
  labels: string[];
  dateRange: {
    start?: string;
    end?: string;
  };
  customFilters: Record<string, any>;
  relationshipFilters: RelationshipFilterData[];
  facilityOwnerFilter: FacilityOwnerFilterData;
  projectOrganizationFilter: ProjectOrganizationFilterData;
}

const SearchView: React.FC = () => {
  // Notification system
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // URL search params for entity detail view integration
  const [searchParams, setSearchParams] = useSearchParams();

  // Create a filtered version of searchParams that excludes detail view parameters
  // This prevents search reloads when only detail_entity_id or detail_relationship_id changes
  const searchParamsForFilters = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_entity_id");
    params.delete("detail_relationship_id");
    return params;
  }, [
    // Only recalculate when the string representation of filter params changes
    // This prevents recalculation when detail params are added/removed
    (() => {
      const params = new URLSearchParams(searchParams);
      params.delete("detail_entity_id");
      params.delete("detail_relationship_id");
      return params.toString();
    })(),
  ]);

  // Derive filters from URL parameters (single source of truth)
  const filters = useMemo<SearchFilters>(
    () => ({
      searchTerm: searchParamsForFilters.get("search") || "",
      entityType:
        (searchParamsForFilters.get("entityType") as EntityType) ||
        "organizations",
      regions: searchParamsForFilters.get("regions")
        ? searchParamsForFilters.get("regions")!.split(",")
        : [],
      industries: searchParamsForFilters.get("industries")
        ? searchParamsForFilters.get("industries")!.split(",")
        : [],
      facilityTypes: (() => {
        const facilityTypesParam = searchParamsForFilters.get("facilityTypes");
        if (!facilityTypesParam) return [];

        const typeValues = facilityTypesParam.split(",");
        // Convert from database values to display names for UI
        try {
          return getDisplayNamesFromDbValues(typeValues);
        } catch {
          return typeValues; // Fallback if conversion fails
        }
      })(),
      organizationTypes: (() => {
        const orgTypesParam = searchParamsForFilters.get("organizationTypes");
        if (!orgTypesParam) return [];

        const typeValues = orgTypesParam.split(",");
        // Convert from database values to display names for UI
        try {
          return getOrgCategoryDisplayNames(typeValues);
        } catch {
          return typeValues; // Fallback if conversion fails
        }
      })(),
      projectTypes: [],
      sourceTypes: searchParamsForFilters.get("sourceTypes")
        ? searchParamsForFilters.get("sourceTypes")!.split(",")
        : [],
      domains: searchParamsForFilters.get("domains")
        ? searchParamsForFilters.get("domains")!.split(",")
        : [],
      labels: searchParamsForFilters.get("labels")
        ? searchParamsForFilters.get("labels")!.split(",")
        : [],
      dateRange: {
        start: searchParamsForFilters.get("dateStart") || undefined,
        end: searchParamsForFilters.get("dateEnd") || undefined,
      },
      customFilters: {
        naicsCodes: searchParamsForFilters.get("naics")
          ? searchParamsForFilters.get("naics")!.split(",")
          : [],
        inputHSCodes: searchParamsForFilters.get("hsInput")
          ? searchParamsForFilters.get("hsInput")!.split(",")
          : [],
        outputHSCodes: searchParamsForFilters.get("hsOutput")
          ? searchParamsForFilters.get("hsOutput")!.split(",")
          : [],
        homeCountries: searchParamsForFilters.get("homeCountries")
          ? searchParamsForFilters.get("homeCountries")!.split(",")
          : [],
        foundingYearMin:
          searchParamsForFilters.get("foundingYearMin") || undefined,
        foundingYearMax:
          searchParamsForFilters.get("foundingYearMax") || undefined,
        keywords: searchParamsForFilters.get("keywords")
          ? searchParamsForFilters.get("keywords")!.split(",")
          : [],
        projectTypes: searchParamsForFilters.get("projectTypes")
          ? searchParamsForFilters.get("projectTypes")!.split(",")
          : [],
        projectStatuses: searchParamsForFilters.get("projectStatuses")
          ? searchParamsForFilters.get("projectStatuses")!.split(",")
          : [],
        facilityTypes: searchParamsForFilters.get("facilityTypes")
          ? searchParamsForFilters.get("facilityTypes")!.split(",")
          : [],
        facilityStatuses: searchParamsForFilters.get("facilityStatuses")
          ? getFacilityStatusDisplayNames(
              searchParamsForFilters.get("facilityStatuses")!.split(","),
            )
          : [],
        facilityOwners: searchParamsForFilters.get("facilityOwners")
          ? searchParamsForFilters.get("facilityOwners")!.split(",")
          : [],
        suppliers: searchParamsForFilters.get("suppliers")
          ? searchParamsForFilters.get("suppliers")!.split(",")
          : [],
        customers: searchParamsForFilters.get("customers")
          ? searchParamsForFilters.get("customers")!.split(",")
          : [],
      },
      relationshipFilters: (() => {
        const relationshipParam = searchParamsForFilters.get("relationships");
        if (!relationshipParam) return [];
        try {
          return JSON.parse(decodeURIComponent(relationshipParam));
        } catch {
          return [];
        }
      })(),
      facilityOwnerFilter: (() => {
        const ownersParam = searchParamsForFilters.get("facilityOwners");
        if (!ownersParam) return { ownerCompanies: [], maxTiers: 1 };
        try {
          return JSON.parse(decodeURIComponent(ownersParam));
        } catch {
          return { ownerCompanies: [], maxTiers: 1 };
        }
      })(),
      projectOrganizationFilter: (() => {
        const participantsParam = searchParamsForFilters.get(
          "projectParticipants",
        );
        if (!participantsParam)
          return { participantCompanies: [], maxTiers: 1 };
        try {
          return JSON.parse(decodeURIComponent(participantsParam));
        } catch {
          return { participantCompanies: [], maxTiers: 1 };
        }
      })(),
    }),
    [searchParamsForFilters],
  );

  const [showFilters, setShowFilters] = useState(true);
  const [selectedResults, setSelectedResults] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // Save/Load search state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Table pagination state
  const [tablePagination, setTablePagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [tableSorting, setTableSorting] = useState<
    Array<{ id: string; desc: boolean }>
  >(() => {
    const sortBy = searchParams.get("sortBy");
    const sortOrder = searchParams.get("sortOrder");
    if (sortBy) {
      return [{ id: sortBy, desc: sortOrder === "desc" }];
    } else {
      return [];
    }
    // Default sort field based on entity type and whether there's a search query
    // const hasQuery = filters.searchTerm && filters.searchTerm.trim() !== "";
    // const defaultSortField = ["sources", "projects"].includes(
    //   filters.entityType
    // )
    //   ? "publication_date"
    //   : hasQuery
    //     ? undefined // No default sort when there's a query (use relevance)
    //     : "confidence_level"; // Use confidence_level only when no query

    // if (!defaultSortField) {
    //   return []; // Empty array means no sorting (backend will use relevance)
    // }
    // const defaultDesc = true;
    // return [{ id: defaultSortField, desc: defaultDesc }];
  });

  // Map frontend column IDs to backend sort fields based on entity type
  const getSortField = useCallback(
    (columnId: string, entityType: string): string => {
      const commonFields: Record<string, string> = {
        name: "name",
        title: "title",
      };

      const entitySpecificFields: Record<string, Record<string, string>> = {
        organizations: {
          home_country: "home_country",
          industry: "industry",
          employees: "employees",
          founding_year: "founding_year",
        },
        facilities: {
          facility_type: "facility_type",
          status: "status",
          owner: "owner",
          home_country: "home_country",
        },
        projects: {
          project_type: "project_type",
          status: "status",
          project_volume_value: "project_volume_value",
          project_start_year: "project_start_year",
          project_end_year: "project_end_year",
        },
        sources: {
          publication_date: "publication_date",
          domain: "domain",
          language: "language",
          authors: "authors",
        },
      };

      return (
        commonFields[columnId] ||
        entitySpecificFields[entityType]?.[columnId] ||
        columnId
      );
    },
    [],
  );

  // Build the full API filter object from UI state (used as TanStack Query key)
  const apiSearchFilters = useMemo<ApiSearchFilters>(() => {
    const offset = tablePagination.pageIndex * tablePagination.pageSize;
    const currentSort = tableSorting.length > 0 ? tableSorting[0] : null;

    const searchFilters: ApiSearchFilters = {
      q: filters.searchTerm,
      entity_type: filters.entityType,
      limit: tablePagination.pageSize,
      offset: offset,
    };

    if (currentSort) {
      const backendSortField = getSortField(currentSort.id, filters.entityType);
      searchFilters.sort = backendSortField;
      searchFilters.order = currentSort.desc ? "desc" : "asc";
    }

    if (filters.dateRange.start || filters.dateRange.end) {
      searchFilters.updated_date_start = filters.dateRange.start;
      searchFilters.updated_date_end = filters.dateRange.end;
    }

    // Add organization-specific filters
    if (filters.entityType === "organizations") {
      if (filters.customFilters?.homeCountries?.length) {
        const countryCodes = regionMapping.getCountryCodesFromIds(
          filters.customFilters.homeCountries,
        );
        if (countryCodes.length > 0) {
          searchFilters.home_country = countryCodes;
        }
      }
      if (filters.customFilters?.naicsCodes?.length) {
        searchFilters.naics = filters.customFilters.naicsCodes;
      }
      if (filters.customFilters?.inputHSCodes?.length) {
        searchFilters.input_hs_codes = filters.customFilters.inputHSCodes;
      }
      if (filters.customFilters?.outputHSCodes?.length) {
        searchFilters.output_hs_codes = filters.customFilters.outputHSCodes;
      }
      if (filters.customFilters?.foundingYearMin) {
        searchFilters.founding_year_min = parseInt(
          filters.customFilters.foundingYearMin,
        );
      }
      if (filters.customFilters?.foundingYearMax) {
        searchFilters.founding_year_max = parseInt(
          filters.customFilters.foundingYearMax,
        );
      }
      if (filters.organizationTypes?.length) {
        const orgTypeDbValues = getOrgCategoryDbValues(
          filters.organizationTypes,
        );
        searchFilters.organization_types = orgTypeDbValues;
      }

      if (filters.labels?.length > 0) {
        searchFilters.labels = filters.labels;
      }

      // Handle relationship filters as structured objects instead of flattened arrays
      if (filters.relationshipFilters?.length) {
        const relationshipFilters = filters.relationshipFilters
          .filter((filter) => filter.targetCompanies.length > 0)
          .map((filter) => ({
            related_to_entity: filter.targetCompanies.map(
              (company) => company.id,
            ),
            relationship_types:
              filter.relationshipTypes.length > 0
                ? filter.relationshipTypes
                : undefined,
            max_relationship_tiers: filter.maxTiers,
          }));

        if (relationshipFilters.length > 0) {
          searchFilters.relationship_filters = relationshipFilters;
        }
      }
    } else if (filters.entityType === "facilities") {
      if (filters.customFilters?.homeCountries?.length) {
        const countryCodes = regionMapping.getCountryCodesFromIds(
          filters.customFilters.homeCountries,
        );
        if (countryCodes.length > 0) {
          searchFilters.home_country = countryCodes;
        }
      }
      if (filters.facilityTypes.length > 0) {
        const facilityTypeDbValues = getDbValuesFromDisplayNames(
          filters.facilityTypes,
        );
        searchFilters.facility_types = facilityTypeDbValues;
      }
      if (filters.customFilters?.facilityStatuses?.length) {
        const facilityStatusDbValues = getFacilityStatusDbValues(
          filters.customFilters.facilityStatuses,
        );
        searchFilters.facility_statuses = facilityStatusDbValues;
      }
      if (filters.customFilters?.facilityOwners?.length) {
        searchFilters.facility_owners = filters.customFilters.facilityOwners;
      }
      if (filters.customFilters?.suppliers?.length) {
        searchFilters.suppliers = filters.customFilters.suppliers;
      }
      if (filters.customFilters?.customers?.length) {
        searchFilters.customers = filters.customFilters.customers;
      }
      if (filters.customFilters?.naicsCodes?.length) {
        searchFilters.naics = filters.customFilters.naicsCodes;
      }
      if (filters.customFilters?.inputHSCodes?.length) {
        searchFilters.input_hs_codes = filters.customFilters.inputHSCodes;
      }
      if (filters.customFilters?.outputHSCodes?.length) {
        searchFilters.output_hs_codes = filters.customFilters.outputHSCodes;
      }
      // Add facility owner filter
      if (filters.facilityOwnerFilter?.ownerCompanies?.length) {
        const ownerIds = filters.facilityOwnerFilter.ownerCompanies.map(
          (company) => company.id,
        );
        searchFilters.facility_owners = ownerIds;
        searchFilters.facility_owner_max_tiers =
          filters.facilityOwnerFilter.maxTiers;
      } else {
        searchFilters.facility_owners = undefined;
        searchFilters.facility_owner_max_tiers = undefined;
      }
      // Add project organization filter
      // {
      //   // Explicitly ensure project organization parameters are undefined when no companies selected
      //   searchFilters.participating_organizations = undefined;
      //   searchFilters.project_organization_max_tiers = undefined;
      // }
      if (filters.labels?.length > 0) {
        searchFilters.labels = filters.labels;
      }
    } else if (filters.entityType === "projects") {
      if (filters.customFilters?.homeCountries?.length) {
        const countryCodes = regionMapping.getCountryCodesFromIds(
          filters.customFilters.homeCountries,
        );
        if (countryCodes.length > 0) {
          searchFilters.home_country = countryCodes;
        }
      }
      if (filters.customFilters?.naicsCodes?.length) {
        searchFilters.naics = filters.customFilters.naicsCodes;
      }
      if (filters.customFilters?.projectTypes?.length) {
        const projectTypeKeys = projectTypeMapping.getProjectTypeKeys(
          filters.customFilters.projectTypes,
        );
        if (projectTypeKeys.length > 0) {
          searchFilters.project_types = projectTypeKeys;
        }
      }

      // Add project status filter
      if (filters.customFilters?.projectStatuses?.length) {
        searchFilters.project_statuses = filters.customFilters.projectStatuses;
      }

      // Add project organization filter
      if (filters.projectOrganizationFilter?.participantCompanies?.length) {
        const participantIds =
          filters.projectOrganizationFilter.participantCompanies.map(
            (company) => company.id,
          );
        searchFilters.participating_organizations = participantIds;
        searchFilters.project_organization_max_tiers =
          filters.projectOrganizationFilter.maxTiers;
      } else {
        searchFilters.participating_organizations = undefined;
        searchFilters.project_organization_max_tiers = undefined;
      }
    } else if (filters.entityType === "sources") {
      if (filters.domains.length > 0) {
        searchFilters.domains = filters.domains;
      }
    }

    return searchFilters;
  }, [filters, tablePagination, tableSorting, getSortField]);

  // Fetch search results via TanStack Query
  const searchQuery = useSearchResults({
    filters: apiSearchFilters,
    entityType: filters.entityType,
  });

  // `SearchResponse.results` is `any[]`; annotate so the `?? []` fallback doesn't
  // widen to `any[] | never[]`, which breaks callback inference (TS7006) in the
  // expand/collapse helpers below.
  const searchResults: any[] = searchQuery.data?.results ?? [];
  const isLoading = searchQuery.isLoading || searchQuery.isFetching;
  const totalResults =
    searchQuery.data?.pagination?.total_count ??
    searchQuery.data?.results?.length ??
    0;
  const pagination = searchQuery.data?.pagination ?? null;
  const error = searchQuery.error
    ? searchQuery.error instanceof Error
      ? searchQuery.error.message
      : "Search failed"
    : null;

  // Helper function to update URL params for filters (using shared utility)
  const handleFilterUpdate = useCallback(
    (filterKey: keyof SearchFilters, value: any) => {
      updateFilterInURL(filterKey, value, searchParams, setSearchParams, {
        getDbValuesFromDisplayNames: getDbValuesFromDisplayNames,
        getFacilityStatusDbValues: getFacilityStatusDbValues,
        getOrgCategoryDbValues: getOrgCategoryDbValues,
      });
      // Reset to first page when filters change (same pattern as handleSortingChange)
      setTablePagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [searchParams, setSearchParams],
  );

  // Handle table pagination changes
  const handlePaginationChange = useCallback(
    (updater: any) => {
      const newPagination =
        typeof updater === "function" ? updater(tablePagination) : updater;

      setTablePagination(newPagination);

      // Reset scroll to top when page changes (same pattern as filter updates)
      if (tableContainerRef.current) {
        const scrollableElement = tableContainerRef.current.querySelector(
          ".MuiTableContainer-root",
        ) as HTMLElement;
        if (scrollableElement) {
          scrollableElement.scrollTop = 0;
        }
      }
      // Search will be triggered by useEffect dependency on tablePagination
    },
    [tablePagination],
  );

  // Handle table sorting changes
  const handleSortingChange = useCallback(
    (updater: any) => {
      const newSorting =
        typeof updater === "function" ? updater(tableSorting) : updater;

      setTableSorting(newSorting);
      // Reset to first page when sorting changes
      setTablePagination((prev) => ({ ...prev, pageIndex: 0 }));
      // Search will be triggered by useEffect dependency on tableSorting
    },
    [tableSorting],
  );

  // Search handlers for memoized input
  const handleSearchSubmit = useCallback(
    (searchTerm: string) => {
      handleFilterUpdate("searchTerm", searchTerm);
    },
    [handleFilterUpdate],
  );

  const handleSearchClear = useCallback(() => {
    handleFilterUpdate("searchTerm", "");
  }, [handleFilterUpdate]);

  // Handle entity type change
  const handleEntityTypeChange = useCallback(
    (newEntityType: EntityType) => {
      // Clear all filters and set new entity type
      // Note: This also closes any open detail overlays by not including detail_entity_id or detail_relationship_id
      // const params = new URLSearchParams();
      const params = searchParams;
      if (newEntityType !== "organizations") {
        params.set("entityType", newEntityType);
      } else {
        params.delete("entityType");
      }
      setSearchParams(params, { replace: true });

      // Reset pagination when changing entity type
      setTablePagination({ pageIndex: 0, pageSize: 50 });
      // Reset sorting to appropriate default for entity type
      const hasQuery = filters.searchTerm && filters.searchTerm.trim() !== "";
      setTableSorting([]); // Empty array means no sorting (backend will use relevance)
      setSelectedResults([]);
      setExpandedRows({});
    },
    [setSearchParams],
  );

  const handleFilterChange = useCallback(
    (filterKey: keyof SearchFilters, value: any) => {
      handleFilterUpdate(filterKey, value);
    },
    [handleFilterUpdate],
  );

  // Entity click handler for opening detail view
  const handleEntityClick = useCallback(
    (entityId: string) => {
      const params = new URLSearchParams(searchParams);
      params.delete("detail_relationship_id"); // Clear relationship overlay
      params.set("detail_entity_id", entityId);
      setSearchParams(params, { replace: false }); // Create history entry
    },
    [searchParams, setSearchParams],
  );

  const clearAllFilters = useCallback(() => {
    clearAllFiltersFromURL(filters.entityType, searchParams, setSearchParams);
    setExpandedRows({});
  }, [filters.entityType, searchParams, setSearchParams]);

  // Use shared utility for extracting URL params for saving

  // Save search handler
  const handleSaveSearch = useCallback(
    async (name: string, description?: string) => {
      try {
        await createSavedSearch({
          name,
          description,
          search_type: filters.entityType,
          filters: getSearchParamsForSave(searchParams),
        });
        setShowSaveDialog(false);
        showNotification(`Search "${name}" saved successfully`, "success");
      } catch (error) {
        showNotification("Failed to save search. Please try again.", "error");
        throw error;
      }
    },
    [filters.entityType, searchParams, showNotification],
  );

  // Load search handler
  const handleLoadSearch = useCallback(
    (search: SavedSearchData) => {
      // Create new URLSearchParams from saved filters
      const newParams = new URLSearchParams(search.filters);

      // Preserve detail view params if they exist
      const detailEntityId = searchParams.get("detail_entity_id");
      const detailRelationId = searchParams.get("detail_relation_id");
      if (detailEntityId) newParams.set("detail_entity_id", detailEntityId);
      if (detailRelationId)
        newParams.set("detail_relation_id", detailRelationId);

      // Navigate to the new URL - this will reload the component with new filters
      navigate(`/search?${newParams.toString()}`);
      setShowLoadDialog(false);
    },
    [searchParams, navigate],
  );

  // Delete search handler
  const handleDeleteSearch = useCallback(async (id: string) => {
    try {
      await deleteSavedSearch(id);
    } catch (error) {
      throw error;
    }
  }, []);

  // Expand/collapse all functionality
  const handleExpandAll = useCallback(() => {
    const allRowIds = searchResults.map((row) => row.id);
    const newExpandedState = allRowIds.reduce(
      (acc, id) => ({ ...acc, [id]: true }),
      {},
    );
    setExpandedRows(newExpandedState);
  }, [searchResults]);

  const handleCollapseAll = useCallback(() => {
    setExpandedRows({});
  }, []);

  const isAllExpanded = useMemo(() => {
    if (searchResults.length === 0) return false;
    return searchResults.every((row) => expandedRows[row.id]);
  }, [searchResults, expandedRows]);

  const isAnyExpanded = useMemo(() => {
    return Object.values(expandedRows).some((expanded) => expanded);
  }, [expandedRows]);

  // Render detail panel for expanded rows
  const renderDetailPanel = useCallback(
    ({ row }: { row: any }) => {
      const entity = row.original;

      return (
        <Box
          sx={{
            p: 0,
            m: 0,
            width: "100%",
            backgroundColor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ p: 3, width: "100%" }}>
            {filters.entityType === "organizations" && (
              <>
                {/* First Row: Description, NAICS, Input HS, Output HS - 40%/20%/20%/20% Layout */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr", // Single column on mobile
                      sm: "1fr 1fr", // Two columns on small screens
                      md: "2fr 1fr 1fr 1fr", // 40%/20%/20%/20% equivalent on medium+ screens
                    },
                    gap: 3,
                    width: "100%",
                    minWidth: 0,
                    mb: 3, // Margin bottom for spacing to next row
                  }}
                >
                  {/* Description - 40% */}
                  <Box>
                    {/* <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  Description
                </Typography> */}
                    {entity.description ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {Array.isArray(entity.description)
                          ? entity.description.join(", ")
                          : entity.description}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No description available
                      </Typography>
                    )}
                  </Box>

                  {/* NAICS Codes - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      NAICS Codes
                    </Typography>
                    {entity.naics_codes_with_title &&
                    entity.naics_codes_with_title.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.naics_codes_with_title
                          .slice(0, 3)
                          .map((code: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                code.length > 50
                                  ? `${code.substring(0, 50)}...`
                                  : code
                              }
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                backgroundColor: "transparent",
                                borderColor: "primary.main",
                                color: "primary.main",
                                "&:hover": {
                                  backgroundColor: "primary.main",
                                  color: "primary.contrastText",
                                },
                              }}
                            />
                          ))}
                        {entity.naics_codes_with_title.length > 3 && (
                          <Tooltip
                            title={
                              <Box>
                                {entity.naics_codes_with_title
                                  .slice(3)
                                  .map((code: string, idx: number) => (
                                    <div key={idx}>{code}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Chip
                              label={`+${entity.naics_codes_with_title.length - 3}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>

                  {/* Input HS Codes - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Input HS Codes
                    </Typography>
                    {entity.input_hs_codes_with_title &&
                    entity.input_hs_codes_with_title.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.input_hs_codes_with_title
                          .slice(0, 3)
                          .map((code: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                code.length > 50
                                  ? `${code.substring(0, 50)}...`
                                  : code
                              }
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                backgroundColor: "transparent",
                                borderColor: "success.main",
                                color: "success.main",
                                "&:hover": {
                                  backgroundColor: "success.main",
                                  color: "success.contrastText",
                                },
                              }}
                            />
                          ))}
                        {entity.input_hs_codes_with_title.length > 3 && (
                          <Tooltip
                            title={
                              <Box>
                                {entity.input_hs_codes_with_title
                                  .slice(3)
                                  .map((code: string, idx: number) => (
                                    <div key={idx}>{code}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Chip
                              label={`+${entity.input_hs_codes_with_title.length - 3}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>

                  {/* Output HS Codes - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Output HS Codes
                    </Typography>
                    {entity.output_hs_codes_with_title &&
                    entity.output_hs_codes_with_title.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.output_hs_codes_with_title
                          .slice(0, 3)
                          .map((code: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                code.length > 50
                                  ? `${code.substring(0, 50)}...`
                                  : code
                              }
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                backgroundColor: "transparent",
                                borderColor: "warning.main",
                                color: "warning.main",
                                "&:hover": {
                                  backgroundColor: "warning.main",
                                  color: "warning.contrastText",
                                },
                              }}
                            />
                          ))}
                        {entity.output_hs_codes_with_title.length > 3 && (
                          <Tooltip
                            title={
                              <Box>
                                {entity.output_hs_codes_with_title
                                  .slice(3)
                                  .map((code: string, idx: number) => (
                                    <div key={idx}>{code}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Chip
                              label={`+${entity.output_hs_codes_with_title.length - 3}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Second Row: Products and Services - 20%/20%/60% Layout */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr", // Single column on mobile
                      sm: "1fr 1fr 3fr", // 20%/20%/60% on small+ screens
                      md: "1fr 1fr 3fr", // 20%/20%/60% on medium+ screens
                    },
                    gap: 3,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {/* Products - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Products
                    </Typography>
                    {entity.products && entity.products.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.products
                          .slice(0, 4)
                          .map((product: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                product.length > 50
                                  ? `${product.substring(0, 50)}...`
                                  : product
                              }
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                          ))}
                        {entity.products.length > 4 && (
                          <Tooltip
                            title={
                              <Box>
                                {entity.products
                                  .slice(4)
                                  .map((product: string, idx: number) => (
                                    <div key={idx}>{product}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Chip
                              label={`+${entity.products.length - 4}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>

                  {/* Services - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Services
                    </Typography>
                    {entity.services && entity.services.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.services
                          .slice(0, 4)
                          .map((service: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                service.length > 50
                                  ? `${service.substring(0, 50)}...`
                                  : service
                              }
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                          ))}
                        {entity.services.length > 4 && (
                          <Tooltip
                            title={
                              <Box>
                                {entity.services
                                  .slice(4)
                                  .map((service: string, idx: number) => (
                                    <div key={idx}>{service}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Chip
                              label={`+${entity.services.length - 4}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>

                  {/* Empty space - 60% */}
                  <Box></Box>
                </Box>
              </>
            )}

            {filters.entityType === "facilities" && (
              <>
                {/* First Row: Description, NAICS, Input HS, Output HS - 40%/20%/20%/20% Layout */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr", // Single column on mobile
                      sm: "1fr 1fr", // Two columns on small screens
                      md: "2fr 1fr 1fr 1fr", // 40%/20%/20%/20% equivalent on medium+ screens
                    },
                    gap: 3,
                    width: "100%",
                    minWidth: 0,
                    mb: 3, // Margin bottom for spacing to next row
                  }}
                >
                  {/* Description - 40% */}
                  <Box>
                    {entity.description ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {Array.isArray(entity.description)
                          ? entity.description.join(", ")
                          : entity.description}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No description available
                      </Typography>
                    )}
                  </Box>

                  {/* NAICS Codes - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      NAICS Codes
                    </Typography>
                    {entity.naics_codes_with_title &&
                    entity.naics_codes_with_title.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.naics_codes_with_title
                          .slice(0, 3)
                          .map((code: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                code.length > 50
                                  ? `${code.substring(0, 50)}...`
                                  : code
                              }
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                backgroundColor: "transparent",
                                borderColor: "primary.main",
                                color: "primary.main",
                                "&:hover": {
                                  backgroundColor: "primary.main",
                                  color: "primary.contrastText",
                                },
                              }}
                            />
                          ))}
                        {entity.naics_codes_with_title.length > 3 && (
                          <Chip
                            label={`+${entity.naics_codes_with_title.length - 3}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>

                  {/* Input HS Codes - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Input HS Codes
                    </Typography>
                    {entity.input_hs_codes_with_title &&
                    entity.input_hs_codes_with_title.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.input_hs_codes_with_title
                          .slice(0, 3)
                          .map((code: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                code.length > 50
                                  ? `${code.substring(0, 50)}...`
                                  : code
                              }
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                backgroundColor: "transparent",
                                borderColor: "success.main",
                                color: "success.main",
                                "&:hover": {
                                  backgroundColor: "success.main",
                                  color: "success.contrastText",
                                },
                              }}
                            />
                          ))}
                        {entity.input_hs_codes_with_title.length > 3 && (
                          <Chip
                            label={`+${entity.input_hs_codes_with_title.length - 3}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>

                  {/* Output HS Codes - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Output HS Codes
                    </Typography>
                    {entity.output_hs_codes_with_title &&
                    entity.output_hs_codes_with_title.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.output_hs_codes_with_title
                          .slice(0, 3)
                          .map((code: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                code.length > 50
                                  ? `${code.substring(0, 50)}...`
                                  : code
                              }
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                backgroundColor: "transparent",
                                borderColor: "warning.main",
                                color: "warning.main",
                                "&:hover": {
                                  backgroundColor: "warning.main",
                                  color: "warning.contrastText",
                                },
                              }}
                            />
                          ))}
                        {entity.output_hs_codes_with_title.length > 3 && (
                          <Chip
                            label={`+${entity.output_hs_codes_with_title.length - 3}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Second Row: Products and Services - 20%/20%/60% Layout */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr", // Single column on mobile
                      sm: "1fr 1fr 3fr", // 20%/20%/60% on small+ screens
                      md: "1fr 1fr 3fr", // 20%/20%/60% on medium+ screens
                    },
                    gap: 3,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {/* Products - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Products
                    </Typography>
                    {entity.products_produced &&
                    entity.products_produced.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.products_produced
                          .slice(0, 4)
                          .map((product: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                product.length > 50
                                  ? `${product.substring(0, 50)}...`
                                  : product
                              }
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                          ))}
                        {entity.products_produced.length > 4 && (
                          <Chip
                            label={`+${entity.products_produced.length - 4}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>

                  {/* Services - 20% */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Services
                    </Typography>
                    {entity.services && entity.services.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {entity.services
                          .slice(0, 4)
                          .map((service: string, index: number) => (
                            <Chip
                              key={index}
                              label={
                                service.length > 50
                                  ? `${service.substring(0, 50)}...`
                                  : service
                              }
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                          ))}
                        {entity.services.length > 4 && (
                          <Chip
                            label={`+${entity.services.length - 4}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        -
                      </Typography>
                    )}
                  </Box>

                  {/* Empty space - 60% */}
                  <Box></Box>
                </Box>
              </>
            )}

            {filters.entityType === "projects" && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "2fr 1fr 1fr 1fr",
                    },
                    gap: 3,
                    width: "100%",
                    minWidth: 0,
                    mb: 3,
                  }}
                >
                  <Box>
                    {entity.description ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {Array.isArray(entity.description)
                          ? entity.description.join(", ")
                          : entity.description}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No description available
                      </Typography>
                    )}
                  </Box>

                  {/* Empty spaces for remaining 60% */}
                  <Box></Box>
                  <Box></Box>
                  <Box></Box>
                </Box>
              </>
            )}

            {filters.entityType === "sources" && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "2fr 1fr 1fr 1fr",
                    },
                    gap: 3,
                    width: "100%",
                    minWidth: 0,
                    mb: 3,
                  }}
                >
                  <Box>
                    {entity.summary ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {Array.isArray(entity.summary)
                          ? entity.summary.join(", ")
                          : entity.summary}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No summary available
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    {entity.url ? (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5, fontWeight: 500 }}
                        >
                          Article URL
                        </Typography>
                        <Typography
                          component="a"
                          href={entity.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{
                            color: "primary.main",
                            textDecoration: "none",
                            wordBreak: "break-all",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {entity.url}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No URL available
                      </Typography>
                    )}
                  </Box>

                  {/* Empty spaces for remaining columns */}
                  <Box></Box>
                  <Box></Box>
                </Box>
              </>
            )}

            {/* Fallback for other entity types */}
            {filters.entityType !== "organizations" &&
              filters.entityType !== "facilities" &&
              filters.entityType !== "projects" &&
              filters.entityType !== "sources" && (
                <>
                  <Typography variant="h6" gutterBottom>
                    {entity.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>ID:</strong> {entity.id}
                    </Typography>
                    {entity.description && (
                      <Typography variant="body2">
                        <strong>Description:</strong>{" "}
                        {Array.isArray(entity.description)
                          ? entity.description.join(", ")
                          : entity.description}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Entity-specific details will be displayed here based on the
                    entity type.
                  </Typography>
                </>
              )}
          </Box>
        </Box>
      );
    },
    [filters.entityType],
  );

  // Table columns configuration
  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    const baseColumns: MRT_ColumnDef<any>[] = [];

    // Add entity-specific columns based on selected entity type
    if (filters.entityType === "organizations") {
      baseColumns.push(
        {
          accessorKey: "name",
          header: "Name",
          size: 250,
          Cell: ({ cell, row }) => {
            const name = String(cell.getValue() || "");
            return (
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{
                  cursor: "pointer",
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.light",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEntityClick(row.original.id);
                }}
                onMouseDown={(e) => {
                  if (e.button === 1) {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(`/entity/${row.original.id}`, "_blank");
                  }
                }}
              >
                {name}
              </Typography>
            );
          },
        },
        {
          accessorKey: "home_country",
          header: "Country",
          size: 100,
          Cell: ({ cell }) => {
            const countries = cell.getValue() as string | string[];

            // Handle both single string and array of strings
            const countryList = Array.isArray(countries)
              ? countries
              : [countries].filter(Boolean);

            if (countryList.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {countryList
                  .slice(0, 2)
                  .map((country: string, index: number) => (
                    <Chip
                      key={index}
                      label={country}
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ))}
                {countryList.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {countryList
                          .slice(2)
                          .map((country: string, idx: number) => (
                            <div key={idx}>{country}</div>
                          ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${countryList.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "organization_type",
          header: "Type",
          size: 150,
          Cell: ({ cell }) => {
            const types = cell.getValue() as string[];
            if (!types || !Array.isArray(types) || types.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            const displayNames = getOrgCategoryDisplayNames(types);
            if (displayNames.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {displayNames.slice(0, 2).map((type: string, index: number) => (
                  <Chip
                    key={index}
                    label={type}
                    size="small"
                    variant="filled"
                    sx={{ fontSize: "0.75rem" }}
                  />
                ))}
                {displayNames.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {displayNames
                          .slice(2)
                          .map((type: string, idx: number) => (
                            <div key={idx}>{type}</div>
                          ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${displayNames.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "status_labels",
          header: "Status",
          size: 150,
          Cell: ({ cell }) => {
            const statusLabels = cell.getValue() as string[] | string;

            // Normalize to array
            const statusArray = Array.isArray(statusLabels)
              ? statusLabels
              : typeof statusLabels === "string"
                ? [statusLabels]
                : [];

            // Show up to 2 status labels
            const displayStatuses = statusArray.slice(0, 2);

            if (displayStatuses.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {displayStatuses.map((status, index) => {
                  const displayName = (() => {
                    try {
                      return getOrgStatusDisplayName(status);
                    } catch {
                      return status;
                    }
                  })();

                  return (
                    <Chip
                      key={index}
                      label={displayName}
                      size="small"
                      variant="outlined"
                      color={getOrgStatusColor(status)}
                      sx={{ fontSize: "0.75rem" }}
                    />
                  );
                })}
                {statusArray.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {statusArray.slice(2).map((status, idx) => {
                          try {
                            return (
                              <div key={idx}>
                                {getOrgStatusDisplayName(status)}
                              </div>
                            );
                          } catch {
                            return <div key={idx}>{status}</div>;
                          }
                        })}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${statusArray.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "founding_year",
          header: "Founded",
          size: 100,
          Cell: ({ cell }) => {
            const year = cell.getValue() as number;
            return (
              <Typography variant="body2">
                {year ? year.toString() : ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "websites",
          header: "Websites",
          size: 300,
          Cell: ({ cell }) => {
            const websites = cell.getValue() as string[];
            if (
              !websites ||
              !Array.isArray(websites) ||
              websites.length === 0
            ) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {websites.slice(0, 3).map((website: string, index: number) => (
                  <Chip
                    key={index}
                    label={website.replace(/^https?:\/\//, "")}
                    size="small"
                    variant="outlined"
                    component="a"
                    href={
                      website.startsWith("http")
                        ? website
                        : `https://${website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    sx={{
                      fontSize: "0.7rem",
                      height: 20,
                      backgroundColor: "transparent",
                      borderColor: "divider",
                      color: "text.secondary",
                      textDecoration: "none",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        borderColor: "primary.main",
                        color: "primary.main",
                      },
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "120px",
                      },
                    }}
                  />
                ))}
                {websites.length > 3 && (
                  <Tooltip
                    title={
                      <Box>
                        {websites
                          .slice(3)
                          .map((website: string, idx: number) => (
                            <div key={idx}>{website}</div>
                          ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${websites.length - 3}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.7rem",
                        height: 20,
                        backgroundColor: "transparent",
                        borderColor: "divider",
                        color: "text.secondary",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        // {
        //   accessorKey: "directories",
        //   header: "Social Media",
        //   size: 180,
        //   Cell: ({ cell }) => {
        //     const directories = cell.getValue() as string[];
        //     if (
        //       !directories ||
        //       !Array.isArray(directories) ||
        //       directories.length === 0
        //     ) {
        //       return (
        //         <Typography variant="body2" color="text.secondary"></Typography>
        //       );
        //     }
        //     return (
        //       <SocialMediaIcons
        //         directories={directories}
        //         maxIconsPerPlatform={2}
        //       />
        //     );
        //   },
        // },
        {
          accessorKey: "labels",
          header: "Labels",
          size: 200,
          Cell: ({ cell }) => {
            const labels = cell.getValue() as string[];
            if (!labels || !Array.isArray(labels) || labels.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {labels.slice(0, 3).map((label: string, index: number) => {
                  const labelDisplay = getLabelDisplay(label);
                  return (
                    <Tooltip key={index} title={labelDisplay.full}>
                      <Chip
                        label={labelDisplay.abbrev}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.75rem" }}
                      />
                    </Tooltip>
                  );
                })}
                {labels.length > 3 && (
                  <Tooltip
                    title={
                      <Box>
                        {labels.slice(3).map((label: string, idx: number) => {
                          const labelDisplay = getLabelDisplay(label);
                          return <div key={idx}>{labelDisplay.full}</div>;
                        })}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${labels.length - 3}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "match_score",
          header: "Match Score",
          size: 100,
          Cell: ({ cell }) => {
            const matchScore = cell.getValue() as number;
            if (matchScore === null || matchScore === undefined) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return (
              <ConfidenceIndicator confidence={matchScore} colorScheme="gray" />
            );
          },
        },
      );
    } else if (filters.entityType === "facilities") {
      baseColumns.push(
        {
          accessorKey: "name",
          header: "Name",
          size: 220,
          Cell: ({ cell, row }) => {
            const name = String(cell.getValue() || "");
            return (
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{
                  cursor: "pointer",
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.light",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEntityClick(row.original.id);
                }}
                onMouseDown={(e) => {
                  if (e.button === 1) {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(`/entity/${row.original.id}`, "_blank");
                  }
                }}
              >
                {name}
              </Typography>
            );
          },
        },
        {
          accessorKey: "facility_type",
          header: "Type",
          size: 180,
          Cell: ({ cell }) => {
            const facilityType = cell.getValue() as string | string[];

            // Handle both single string and array of strings
            const types = Array.isArray(facilityType)
              ? facilityType
              : [facilityType].filter(Boolean);

            if (types.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            // Convert database values to display names
            const displayTypes = getFacilityTypeDisplayNames(types);

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {displayTypes.slice(0, 2).map((type: string, index: number) => (
                  <Chip
                    key={index}
                    label={type}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: "0.75rem" }}
                  />
                ))}
                {displayTypes.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {displayTypes
                          .slice(2)
                          .map((type: string, idx: number) => (
                            <div key={idx}>{type}</div>
                          ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${displayTypes.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "address",
          header: "Address",
          size: 140,
          Cell: ({ cell }) => {
            const address = String(cell.getValue() || "");
            return (
              <Tooltip title={address} placement="top">
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "help",
                  }}
                >
                  {address}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          accessorKey: "country_iso2",
          header: "Country",
          size: 80,
          Cell: ({ cell }) => {
            const countries = cell.getValue() as string | string[];

            // Handle both single string and array of strings
            const countryList = Array.isArray(countries)
              ? countries
              : [countries].filter(Boolean);

            if (countryList.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {countryList
                  .slice(0, 2)
                  .map((country: string, index: number) => (
                    <Chip
                      key={index}
                      label={country}
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ))}
                {countryList.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {countryList
                          .slice(2)
                          .map((country: string, idx: number) => (
                            <div key={idx}>{country}</div>
                          ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${countryList.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "status_labels",
          header: "Status",
          size: 140,
          Cell: ({ cell }) => {
            const statusLabels = cell.getValue() as string[] | string;

            // Normalize to array
            const statusArray = Array.isArray(statusLabels)
              ? statusLabels
              : typeof statusLabels === "string"
                ? [statusLabels]
                : [];

            // Show up to 2 status labels
            const displayStatuses = statusArray.slice(0, 2);

            if (displayStatuses.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            const getStatusColor = (status: string) => {
              if (status === "active" || status === "opened") return "success";
              if (
                status === "closed" ||
                status === "cancelled" ||
                status === "demolished"
              )
                return "error";
              if (
                status === "planned" ||
                status === "under_construction" ||
                status === "expansion_planned" ||
                status === "expanding" ||
                status === "at_risk"
              )
                return "warning";
              if (
                status === "paused" ||
                status === "capacity_reduction" ||
                status === "capacity_reduction_planned"
              )
                return "info";
              return "default";
            };

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {displayStatuses.map((status, index) => {
                  const displayName = (() => {
                    try {
                      return getFacilityStatusDisplayNames([status])[0];
                    } catch {
                      return status;
                    }
                  })();

                  return (
                    <Chip
                      key={index}
                      label={displayName}
                      size="small"
                      variant="outlined"
                      color={getStatusColor(status)}
                      sx={{ fontSize: "0.75rem" }}
                    />
                  );
                })}
                {statusArray.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {statusArray.slice(2).map((status, idx) => {
                          try {
                            return (
                              <div key={idx}>
                                {getFacilityStatusDisplayNames([status])[0]}
                              </div>
                            );
                          } catch {
                            return <div key={idx}>{status}</div>;
                          }
                        })}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${statusArray.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        // {
        //   accessorKey: 'description',
        //   header: 'Description',
        //   size: 200,
        //   Cell: ({ cell }) => (
        //     <Typography variant="body2">
        //       {String(cell.getValue() || '')}
        //     </Typography>
        //   ),
        // }
        {
          accessorKey: "labels",
          header: "Labels",
          size: 200,
          Cell: ({ cell }) => {
            const labels = cell.getValue() as string[];
            if (!labels || !Array.isArray(labels) || labels.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {labels.slice(0, 3).map((label: string, index: number) => {
                  const labelDisplay = getLabelDisplay(label);
                  return (
                    <Tooltip key={index} title={labelDisplay.full}>
                      <Chip
                        label={labelDisplay.abbrev}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.75rem" }}
                      />
                    </Tooltip>
                  );
                })}
                {labels.length > 3 && (
                  <Tooltip
                    title={
                      <Box>
                        {labels.slice(3).map((label: string, idx: number) => {
                          const labelDisplay = getLabelDisplay(label);
                          return <div key={idx}>{labelDisplay.full}</div>;
                        })}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${labels.length - 3}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "match_score",
          header: "Match Score",
          size: 100,
          Cell: ({ cell }) => {
            const matchScore = cell.getValue() as number;
            if (matchScore === null || matchScore === undefined) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return (
              <ConfidenceIndicator confidence={matchScore} colorScheme="gray" />
            );
          },
        },
      );
    } else if (filters.entityType === "projects") {
      baseColumns.push(
        {
          accessorKey: "name",
          header: "Name",
          size: 250,
          Cell: ({ cell, row }) => {
            const title = String(cell.getValue() || "");
            return (
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{
                  cursor: "pointer",
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.light",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEntityClick(row.original.id);
                }}
                onMouseDown={(e) => {
                  if (e.button === 1) {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(`/entity/${row.original.id}`, "_blank");
                  }
                }}
              >
                {title}
              </Typography>
            );
          },
        },
        {
          accessorKey: "project_type",
          header: "Type",
          size: 150,
          Cell: ({ cell }) => {
            const projectType = cell.getValue() as string | string[];

            // Handle both single string and array of strings
            const types = Array.isArray(projectType)
              ? projectType
              : [projectType].filter(Boolean);

            if (types.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            // Convert database values to display names
            const displayTypes = types.map((type) => {
              try {
                return projectTypeMapping.getDisplayName(type);
              } catch {
                return type; // Fallback to original value if mapping fails
              }
            });

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {displayTypes.slice(0, 2).map((type: string, index: number) => (
                  <Chip
                    key={index}
                    label={type}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: "0.75rem" }}
                  />
                ))}
                {displayTypes.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {displayTypes
                          .slice(2)
                          .map((type: string, idx: number) => (
                            <div key={idx}>{type}</div>
                          ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${displayTypes.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "project_status",
          header: "Status",
          size: 180,
          Cell: ({ cell, row }) => {
            const statusLabels = cell.getValue() as string[] | string;

            // Handle both single string and array of strings
            const statusList = Array.isArray(statusLabels)
              ? statusLabels
              : [statusLabels].filter(Boolean);

            // If no status, show placeholder
            if (statusList.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            // Display multiple status chips
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {statusList
                  .slice(0, 2)
                  .map((statusId: string, index: number) => (
                    <ProjectStatusChip
                      key={index}
                      statusId={statusId}
                      lastUpdatedDate={row.original.publication_date}
                      size="small"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ))}
                {statusList.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {statusList
                          .slice(2)
                          .map((statusId: string, idx: number) => (
                            <div key={idx}>{statusId}</div>
                          ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${statusList.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "project_countries",
          header: "Countries",
          size: 150,
          Cell: ({ cell }) => {
            const countries = cell.getValue() as string | string[];

            // Handle both single string and array of strings
            const countryList = Array.isArray(countries)
              ? countries
              : [countries].filter(Boolean);

            if (countryList.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {countryList
                  .slice(0, 2)
                  .map((country: string, index: number) => (
                    <Chip
                      key={index}
                      label={country}
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ))}
                {countryList.length > 2 && (
                  <Tooltip
                    title={
                      <Box>
                        {countryList
                          .slice(2)
                          .map((country: string, idx: number) => (
                            <div key={idx}>{country}</div>
                          ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`+${countryList.length - 2}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: "0.75rem", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          accessorKey: "publication_date",
          header: "Last Updated",
          size: 100,
          Cell: ({ cell }) => {
            const year = cell.getValue() as number;
            return (
              <Typography variant="body2">
                {year ? year.toString() : ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "project_start",
          header: "Project Start",
          size: 100,
          Cell: ({ cell }) => {
            const year = cell.getValue() as number;
            return (
              <Typography variant="body2">
                {year ? year.toString() : ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "project_end",
          header: "Project End",
          size: 100,
          Cell: ({ cell }) => {
            const year = cell.getValue() as number;
            return (
              <Typography variant="body2">
                {year ? year.toString() : ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "budget",
          header: "Budget",
          size: 120,
          Cell: ({ row }) => {
            const value = row.original.project_volume_value;
            const currency = row.original.project_volume_currency;

            if (!value && value !== 0) {
              return (
                <Typography variant="body2" color="text.secondary"></Typography>
              );
            }

            const displayText = formatProjectVolume(value, currency);

            return <Typography variant="body2">{displayText}</Typography>;
          },
        },
        {
          accessorKey: "match_score",
          header: "Match Score",
          size: 100,
          Cell: ({ cell }) => {
            const matchScore = cell.getValue() as number;
            if (matchScore === null || matchScore === undefined) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return (
              <ConfidenceIndicator confidence={matchScore} colorScheme="gray" />
            );
          },
        },
      );
    } else if (filters.entityType === "sources") {
      baseColumns.push(
        {
          accessorKey: "title",
          header: "Title",
          size: 250,
          Cell: ({ cell, row }) => {
            const title = String(cell.getValue() || "");
            return (
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{
                  cursor: "pointer",
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.light",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEntityClick(row.original.id);
                }}
                onMouseDown={(e) => {
                  if (e.button === 1) {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(`/entity/${row.original.id}`, "_blank");
                  }
                }}
              >
                {title}
              </Typography>
            );
          },
        },
        // {
        //   accessorKey: 'source_type',
        //   header: 'Type',
        //   size: 150,
        //   Cell: ({ cell }) => (
        //     <Typography variant="body2">
        //       {String(cell.getValue() || '')}
        //     </Typography>
        //   ),
        // },
        {
          accessorKey: "publication_date",
          header: "Published",
          size: 80,
          Cell: ({ cell }) => {
            const date = cell.getValue() as string;
            return (
              <Typography variant="body2">
                {date ? new Date(date).toISOString().split("T")[0] : ""}
              </Typography>
            );
          },
        },
        {
          accessorKey: "domain",
          header: "Source",
          size: 80,
          Cell: ({ cell }) => {
            const domain = cell.getValue() as string;
            if (!domain) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return (
              <Chip
                label={domain}
                size="small"
                variant="outlined"
                color="info"
                sx={{ fontSize: "0.75rem" }}
              />
            );
          },
        },
        {
          accessorKey: "language",
          header: "Language",
          size: 80,
          Cell: ({ cell }) => {
            const language = cell.getValue() as string;
            if (!language) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return (
              <Chip
                label={language}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: "0.75rem" }}
              />
            );
          },
        },
        {
          accessorKey: "authors",
          header: "Author",
          size: 200,
          Cell: ({ cell }) => {
            const authors = cell.getValue() as string[] | string;
            let authorsText = "";

            if (Array.isArray(authors)) {
              authorsText = authors.join(", ");
            } else {
              authorsText = String(authors || "");
            }

            return (
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {authorsText}
              </Typography>
            );
          },
        },
        {
          accessorKey: "match_score",
          header: "Match Score",
          size: 100,
          Cell: ({ cell }) => {
            const matchScore = cell.getValue() as number;
            if (matchScore === null || matchScore === undefined) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            return (
              <ConfidenceIndicator confidence={matchScore} colorScheme="gray" />
            );
          },
        },
      );
    }

    // Add common columns for all entity types
    // baseColumns.push(
    // );

    return baseColumns;
  }, [filters.entityType, handleEntityClick]);

  // Active filter count for display
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.searchTerm) count++; // Count keyword search
    if (filters.regions.length > 0) count++;
    if (filters.industries.length > 0) count++;
    if (filters.facilityTypes.length > 0) count++;
    if (filters.projectTypes.length > 0) count++;
    if (filters.sourceTypes.length > 0) count++;
    if (filters.dateRange.start || filters.dateRange.end) count++;
    if (filters.labels?.length) count++;

    // Custom filters
    if (filters.customFilters.projectTypes?.length > 0) count++;
    if (filters.customFilters.projectStatuses?.length > 0) count++;
    if (filters.customFilters.facilityStatuses?.length > 0) count++;
    if (filters.customFilters.naicsCodes?.length) count++;
    if (filters.customFilters.inputHSCodes?.length) count++;
    if (filters.customFilters.outputHSCodes?.length) count++;
    if (filters.customFilters.homeCountries?.length) count++;
    if (
      filters.customFilters.foundingYearMin ||
      filters.customFilters.foundingYearMax
    )
      count++;
    if (filters.organizationTypes?.length) count++;
    if (filters.customFilters.keywords?.length) count++;

    // Relationship filters
    if (filters.relationshipFilters?.length > 0) count++;
    if (filters.facilityOwnerFilter?.ownerCompanies?.length > 0) count++;
    if (filters.projectOrganizationFilter?.participantCompanies?.length > 0)
      count++;

    return count;
  }, [filters]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        backgroundColor: "background.default",
      }}
    >
      {/* Filter Sidebar */}
      {showFilters && (
        <SearchFilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          onSaveSearch={() => setShowSaveDialog(true)}
          onLoadSearch={() => setShowLoadDialog(true)}
        />
      )}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          {/* Search Bar */}
          <MemoizedSearchInput
            initialValue={filters.searchTerm}
            onSubmit={handleSearchSubmit}
            onClear={handleSearchClear}
          />

          {/* Controls Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Entity Type Tabs */}
            <Tabs
              value={filters.entityType}
              onChange={(_, value) => handleEntityTypeChange(value)}
              variant="scrollable"
              scrollButtons="auto"
              onClick={(e) => e.stopPropagation()}
            >
              {SEARCHABLE_ENTITY_TYPES.map((entry) => (
                <Tab
                  key={entry.plural}
                  label={entry.pluralLabel}
                  value={entry.plural}
                />
              ))}
            </Tabs>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {/* Expand/Collapse All Button */}
              {searchResults.length > 0 && (
                <Tooltip title={isAllExpanded ? "Collapse All" : "Expand All"}>
                  <Button
                    startIcon={
                      isAllExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    onClick={
                      isAllExpanded ? handleCollapseAll : handleExpandAll
                    }
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: "auto" }}
                  >
                    {isAllExpanded ? "Collapse" : "Expand"} All
                  </Button>
                </Tooltip>
              )}

              <Button
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? "contained" : "outlined"}
                size="small"
              >
                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
              </Button>

              <BulkAddToPortfolioButton
                selectedEntities={
                  selectedResults
                    .map((selectedId) => {
                      const result = searchResults.find(
                        (r) => r.id === selectedId,
                      );
                      if (!result) return null;

                      // Convert plural entity types to singular for backend
                      const entityTypeMap: Record<string, string> = {
                        organizations: "organization",
                        facilities: "facility",
                        projects: "project",
                        sources: "source",
                      };

                      const entityType = result.type || filters.entityType;
                      const singularType =
                        entityTypeMap[entityType] || entityType;

                      return {
                        id: result.id,
                        name: result.name,
                        type: singularType,
                      };
                    })
                    .filter(Boolean) as Array<{
                    id: string;
                    name: string;
                    type: "organization" | "facility" | "project" | "source";
                  }>
                }
                onSuccess={() => {
                  // Clear selection after successful bulk add
                  setSelectedResults([]);
                }}
              />
              {filters.entityType === "organizations" && (
                <BulkAddToTrackButton
                  selectedEntities={
                    selectedResults
                      .map((selectedId) => {
                        const result = searchResults.find(
                          (r) => r.id === selectedId,
                        );
                        if (!result) return null;

                        return {
                          id: result.id,
                          name: result.name,
                          type: "organization" as const,
                        };
                      })
                      .filter(Boolean) as Array<{
                      id: string;
                      name: string;
                      type: "organization" | "facility" | "project";
                    }>
                  }
                  onSuccess={() => {
                    setSelectedResults([]);
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Results Summary */}
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {totalResults > 0
                ? `${totalResults.toLocaleString()} results found`
                : `${searchResults.length} results found`}
            </Typography>

            {searchResults.length > 0 && (
              <ExportFieldsButton
                key={filters.entityType}
                fields={SEARCH_EXPORT_FIELDS[filters.entityType]}
                storageKey={`mi.export.search.${filters.entityType}`}
                title={`Export ${(
                  getEntityTypeByPlural(filters.entityType)?.pluralLabel ??
                  filters.entityType
                ).toLowerCase()}`}
                note={`The ${searchResults.length} results on this page`}
                onExport={(format, keys) =>
                  exportSearchResults(
                    filters.entityType,
                    apiSearchFilters,
                    searchResults,
                    totalResults,
                    format,
                    keys,
                  )
                }
              />
            )}

            {/* Active Filters Display */}
            {activeFilterCount > 0 && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Active filters:
                </Typography>
                {filters.regions.map((region) => (
                  <Chip
                    key={region}
                    label={region}
                    size="small"
                    onDelete={() =>
                      handleFilterChange(
                        "regions",
                        filters.regions.filter((r) => r !== region),
                      )
                    }
                  />
                ))}
                {/* Add more filter chips as needed */}
                <Button
                  size="small"
                  onClick={clearAllFilters}
                  sx={{ minWidth: "auto", p: 0.5 }}
                >
                  Clear all
                </Button>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Results Table */}
        <Box sx={{ flex: 1, overflow: "hidden", width: "100%", minWidth: 0 }}>
          <MaterialReactTable
            key={`${filters.entityType}-${filters.searchTerm}-${JSON.stringify(filters)}`} // Force table reset when filters change
            columns={columns}
            data={searchResults}
            getRowId={(row) => row.id} // Use entity ID instead of row index
            enableSorting={true}
            manualSorting={true}
            onSortingChange={handleSortingChange}
            enableColumnFilters={false}
            enableGlobalFilter={false}
            enablePagination={true}
            enableRowSelection={true}
            enableColumnResizing={true}
            columnResizeMode="onChange"
            enableStickyHeader={true}
            enableBottomToolbar={true}
            enableTopToolbar={false}
            enableExpanding={true}
            positionActionsColumn="first"
            positionExpandColumn="first"
            renderDetailPanel={renderDetailPanel}
            onExpandedChange={(updater: any) => {
              const newExpanded =
                typeof updater === "function" ? updater(expandedRows) : updater;
              setExpandedRows(newExpanded);
            }}
            onRowSelectionChange={(updater) => {
              const newSelection =
                typeof updater === "function"
                  ? updater(
                      selectedResults.reduce(
                        (acc, id) => ({ ...acc, [id]: true }),
                        {},
                      ),
                    )
                  : updater;
              setSelectedResults(
                Object.keys(newSelection).filter((id) => newSelection[id]),
              );
            }}
            manualPagination
            rowCount={totalResults}
            onPaginationChange={handlePaginationChange}
            state={{
              isLoading,
              pagination: tablePagination,
              sorting: tableSorting,
              rowSelection: selectedResults.reduce(
                (acc, id) => ({ ...acc, [id]: true }),
                {},
              ),
              expanded: expandedRows,
            }}
            initialState={{
              density: "compact",
            }}
            displayColumnDefOptions={{
              "mrt-row-select": {
                size: 40,
                muiTableHeadCellProps: {
                  sx: { paddingLeft: "8px", paddingRight: "4px" },
                },
                muiTableBodyCellProps: {
                  sx: { paddingLeft: "8px", paddingRight: "4px" },
                },
              },
              "mrt-row-expand": {
                size: 40,
                muiTableHeadCellProps: {
                  sx: { paddingLeft: "4px", paddingRight: "8px" },
                },
                muiTableBodyCellProps: {
                  sx: { paddingLeft: "4px", paddingRight: "8px" },
                },
              },
            }}
            muiTableProps={{
              sx: {
                "& .MuiTableHead-root": {
                  backgroundColor: "background.paper",
                },
                "& .MuiTableHead-root .MuiTableRow-root": {
                  backgroundColor: "background.paper",
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: "background.paper",
                },
                "& .MuiTableBody-root": {
                  backgroundColor: "background.paper",
                },
                tableLayout: "fixed",
                width: "100%",
                minWidth: "1000px", // Minimum table width to prevent cramping
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                backgroundColor: "transparent",
              },
            }}
            muiTableDetailPanelProps={{
              sx: {
                width: "100% !important",
                maxWidth: "none !important",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                "& td[colspan]": {
                  width: "100% !important",
                  maxWidth: "none !important",
                  padding: "0 !important",
                },
              },
            })}
            muiTablePaperProps={{
              ref: tableContainerRef,
              elevation: 0,
              sx: {
                backgroundColor: "background.paper",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                width: "100%",
              },
            }}
          />
        </Box>
      </Box>

      {/* Save Search Dialog */}
      <SaveSearchDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={handleSaveSearch}
        entityType={filters.entityType}
        activeFilterCount={activeFilterCount}
      />

      {/* Load Search Dialog */}
      <LoadSearchDialog
        open={showLoadDialog}
        onClose={() => setShowLoadDialog(false)}
        onLoad={handleLoadSearch}
        onDelete={handleDeleteSearch}
        entityType={filters.entityType}
      />
    </Box>
  );
};

export default SearchView;
